import { Suspense, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopHeader } from "./TopHeader";
import { useApp } from "@/context/AppContext";
import { findNavItem, canOpen, homePathFor } from "@/lib/nav";
import { Loader2, ShieldOff } from "lucide-react";

// Shown in the page area while a page's file downloads.
function PageLoading() {
  return (
    <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground" role="status" data-testid="page-loading">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
    </div>
  );
}
import { IdleLogout } from "./IdleLogout";

// Typed a URL your role does not own? The page is refused here, not just
// hidden from the menu — the API refuses the same calls with 403.
function NoAccess({ item }) {
  const { session } = useApp();
  const home = homePathFor(session);
  return (
    <div className="mx-auto max-w-lg py-16 text-center" data-testid="no-access">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-600"><ShieldOff className="h-7 w-7" /></div>
      <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">No access to {item?.label || "this page"}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Your role, <span className="font-semibold text-foreground">{session?.roleLabel}</span>, does not include this module.
        Ask a Super Admin if you need it added to your role or scope.
      </p>
      <div className="mt-6 flex justify-center gap-2">
        <Link to={home} className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground">{home === "/app/dashboard" ? "Go to my dashboard" : "Go to my home page"}</Link>
        <Link to="/app/help" className="inline-flex h-9 items-center rounded-md border bg-card px-4 text-sm font-semibold">What can I access?</Link>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { session, allowed } = useApp();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  // Below the lg breakpoint the sidebar is an off-canvas drawer opened from the header.
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!session) return <Navigate to="/" replace />;

  const item = findNavItem(location.pathname);
  const blocked = !canOpen(item, allowed);

  // A role without the dashboard (a partner radiology centre) has no use for a
  // refusal screen that points back at the dashboard: /app/dashboard, or any
  // other page outside its menu, takes it to its own landing page instead.
  // Roles that own the dashboard keep the "No access" page, as before.
  const home = homePathFor(session);
  if (blocked && session.permissions && !allowed("home") && location.pathname !== home) {
    return <Navigate to={home} replace />;
  }

  return (
    <div className="app-ground flex min-h-screen">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopHeader onMenu={() => setMobileOpen(true)} />
        {/* Full width of the screen beside the sidebar (the user's call, 2026-10-09): no centred max-width
            band, a slim gutter. */}
        <main className="min-w-0 flex-1 px-3 py-4 sm:px-4 lg:px-5 lg:py-5">
          <div className="w-full animate-fade-up">
            {/* Each page is its own file, fetched when opened (lib/lazyPage.js): the menu stays while it loads. */}
            {blocked ? <NoAccess item={item} /> : <Suspense fallback={<PageLoading />}><Outlet /></Suspense>}
          </div>
        </main>
      </div>
      <IdleLogout />
    </div>
  );
}
